import React from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addWeeks, subWeeks, addMonths, subMonths, getWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Users, Home } from 'lucide-react';

interface DailySchedule {
  date: string;
  weekday: string;
  day_type: string;
  presence: { [key: string]: boolean };
  kids_present: string[];
  total_kids_present: number;
  // Every one of the family's kids is home (set by FamilyScheduleFixed)
  all_kids_present?: boolean;
}

const isWeekendDate = (date: string) => {
  const day = new Date(date + 'T12:00:00').getDay();
  return day === 0 || day === 6;
};

interface CalendarEvent {
  id?: string;
  event_name?: string;
  event_date?: string;
  start_date?: string;
  end_date?: string;
  event_type?: string;
  description?: string;
  calendar_type?: 'school' | 'district';
  calendar_id?: string;
  district_name?: string;
  school_system?: string;
}

interface ViewProps {
  dailySchedules: DailySchedule[];
  currentDate: Date;
  onDateChange: (date: Date) => void;
  familyMembers: Array<{
    username: string;
    display_name: string;
    color: string;
    emoji: string;
  }>;
  calendarEvents?: CalendarEvent[];
}

export function WeeklyView({ dailySchedules, currentDate, onDateChange, familyMembers, calendarEvents = [] }: ViewProps) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getScheduleForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return dailySchedules.find(s => s.date === dateStr);
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarEvents.filter(event => {
      const eventDate = event.event_date || event.start_date;
      if (!eventDate) return false;

      // Check if event falls on this date
      const eventStartDate = new Date(eventDate + 'T12:00:00');
      const eventEndDate = event.end_date ? new Date(event.end_date + 'T12:00:00') : eventStartDate;
      const checkDate = new Date(dateStr + 'T12:00:00');

      return checkDate >= eventStartDate && checkDate <= eventEndDate;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Week Header - Navigation removed to avoid duplication with CalendarNavigation */}
      <div className="mb-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold">
            Week {getWeek(currentDate)} - {format(weekStart, 'MMM d')} to {format(weekEnd, 'MMM d, yyyy')}
          </h3>
        </div>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {/* Day Headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center font-semibold text-xs sm:text-sm text-gray-600 pb-1 sm:pb-2">
            {day.slice(0, 3)}
          </div>
        ))}

        {/* Day Cells */}
        {weekDays.map(day => {
          const schedule = getScheduleForDate(day);
          const dayEvents = getEventsForDate(day);
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={day.toString()}
              className={`
                border rounded-lg p-2 sm:p-3 min-h-[100px] sm:min-h-[120px]
                ${isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                ${schedule?.all_kids_present ? 'bg-green-50' : ''}
              `}
            >
              <div className="font-semibold text-sm mb-2">
                {format(day, 'd')}
              </div>

              {schedule && (
                <>
                  {/* Kid Presence Indicators - Mobile Optimized with Initials */}
                  <div className="flex flex-wrap gap-1 justify-center mb-2">
                    {familyMembers.map(member => {
                      // Get first two letters of first name for initials
                      const initials = member.display_name.split(' ')[0].slice(0, 2).toUpperCase();

                      return schedule.presence[member.username] ? (
                        <div
                          key={member.username}
                          className="relative group"
                          title={member.display_name}
                        >
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shadow-sm border-2 border-white"
                            style={{ backgroundColor: member.color }}
                          >
                            {initials}
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>

                  {/* Summary */}
                  <div className="text-xs text-gray-600">
                    {schedule.all_kids_present && (
                      <span className="font-semibold text-green-600">All Kids! 🎉</span>
                    )}
                    {schedule.total_kids_present === 0 && (
                      <span className="text-gray-400">No kids</span>
                    )}
                    {schedule.all_kids_present && isWeekendDate(schedule.date) && (
                      <div className="text-green-600 font-semibold mt-1">
                        All Kids Weekend
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Calendar Events */}
              {dayEvents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {dayEvents.map((event, idx) => (
                    <div
                      key={idx}
                      className={`text-xs p-1 rounded ${
                        event.calendar_type === 'school'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                      title={event.description || event.event_name}
                    >
                      <div className="font-semibold truncate">
                        {event.event_name}
                      </div>
                      {event.school_system && (
                        <div className="text-xs opacity-75 truncate">
                          {event.school_system}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Week Summary with Legend */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-600 mb-3">This Week's Summary</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {familyMembers.map(member => {
            const daysPresent = weekDays.filter(day => {
              const schedule = getScheduleForDate(day);
              return schedule?.presence[member.username];
            }).length;
            
            return (
              <div key={member.username} className="flex items-center bg-gray-50 rounded-lg p-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg mr-3"
                  style={{ backgroundColor: member.color + '40' }}
                >
                  {member.emoji}
                </div>
                <div>
                  <div className="font-semibold text-sm">{member.display_name}</div>
                  <div className="text-xs text-gray-600">{daysPresent} days</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function MonthlyView({ dailySchedules, currentDate, onDateChange, familyMembers, calendarEvents = [] }: ViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getScheduleForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return dailySchedules.find(s => s.date === dateStr);
  };

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarEvents.filter(event => {
      const eventDate = event.event_date || event.start_date;
      if (!eventDate) return false;

      // Check if event falls on this date
      const eventStartDate = new Date(eventDate + 'T12:00:00');
      const eventEndDate = event.end_date ? new Date(event.end_date + 'T12:00:00') : eventStartDate;
      const checkDate = new Date(dateStr + 'T12:00:00');

      return checkDate >= eventStartDate && checkDate <= eventEndDate;
    });
  };

  // Calculate monthly statistics
  const monthlyStats = familyMembers.reduce((acc, member) => {
    acc[member.username] = {
      totalDays: 0,
      weekendDays: 0,
      weekdays: 0
    };
    return acc;
  }, {} as Record<string, { totalDays: number; weekendDays: number; weekdays: number }>);

  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  monthDays.forEach(day => {
    const schedule = getScheduleForDate(day);
    if (schedule) {
      familyMembers.forEach(member => {
        if (schedule.presence[member.username]) {
          monthlyStats[member.username].totalDays++;
          if (day.getDay() === 0 || day.getDay() === 6) {
            monthlyStats[member.username].weekendDays++;
          } else {
            monthlyStats[member.username].weekdays++;
          }
        }
      });
    }
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Month Header - Navigation removed to avoid duplication with CalendarNavigation */}
      <div className="mb-6">
        <div className="text-center">
          <h3 className="text-xl font-bold">{format(currentDate, 'MMMM yyyy')}</h3>
        </div>
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-7 gap-1 mb-6">
        {/* Day Headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center font-semibold text-xs text-gray-600 pb-1">
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {calendarDays.map(day => {
          const schedule = getScheduleForDate(day);
          const dayEvents = getEventsForDate(day);
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, currentDate);

          return (
            <div
              key={day.toString()}
              className={`
                border p-1 min-h-[80px] text-xs
                ${isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''}
                ${schedule?.all_kids_present && isCurrentMonth ? 'bg-green-50' : ''}
              `}
            >
              <div className="font-semibold mb-1">{format(day, 'd')}</div>

              {schedule && isCurrentMonth && (
                <div className="space-y-1">
                  {/* Compact presence indicators */}
                  <div className="flex flex-wrap gap-0.5">
                    {familyMembers.map(member => (
                      schedule.presence[member.username] && (
                        <div
                          key={member.username}
                          className="w-5 h-5 rounded-full flex items-center justify-center text-xs"
                          style={{ backgroundColor: member.color + '33' }}
                          title={member.display_name}
                        >
                          {member.emoji}
                        </div>
                      )
                    ))}
                  </div>

                  {/* Special indicators */}
                  {schedule.all_kids_present && (
                    <div className="text-center">🎉</div>
                  )}

                </div>
              )}

              {/* Calendar Events */}
              {dayEvents.length > 0 && isCurrentMonth && (
                <div className="mt-1 space-y-0.5">
                  {dayEvents.map((event, idx) => (
                    <div
                      key={idx}
                      className={`text-[10px] px-1 rounded truncate ${
                        event.calendar_type === 'school'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                      title={`${event.event_name} - ${event.school_system || event.district_name || ''}`}
                    >
                      {event.event_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Monthly Summary */}
      <div className="border-t pt-4">
        <h4 className="font-semibold mb-3">Monthly Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {familyMembers.map(member => {
            const stats = monthlyStats[member.username];
            return (
              <div key={member.username} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">{member.emoji}</span>
                  <span className="font-semibold">{member.display_name}</span>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-semibold">{stats.totalDays} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Weekdays:</span>
                    <span>{stats.weekdays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Weekends:</span>
                    <span>{stats.weekendDays}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
          <span>All Kids Weekend</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-50 border border-blue-500 rounded"></div>
          <span>Today</span>
        </div>
        <div className="flex items-center gap-1">
          <span>🎉</span>
          <span>All 4 Kids</span>
        </div>
      </div>
    </div>
  );
}